import axios from 'axios';
import { API_BASE_URL } from './urls';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Every request carries the logged-in user's token — the backend scopes
// balances, positions, trades and broker status to THAT user. Without this,
// every user was silently served the master/admin ("system") data.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('app_token');
  if (token && !(config.params && config.params.token) && !String(config.url).includes('token=')) {
    config.params = { ...(config.params || {}), token };
  }
  return config;
});

const tradingAPI = {
  getStatus: async () => {
    const response = await api.get('/api/status');
    return response.data;
  },

  appLogin: async (userId, password) => {
    const response = await api.post('/api/app/login', { user_id: userId, password });
    return response.data;
  },

  appVerify: async (token) => {
    const response = await api.get(`/api/app/verify?token=${token}`);
    return response.data;
  },

  adminCreateUser: async (userData) => {
    const response = await api.post('/api/admin/create-user', userData);
    return response.data;
  },

  adminListUsers: async () => {
    const response = await api.get('/api/admin/users');
    return response.data;
  },

  adminDeleteUser: async (userId) => {
    const response = await api.delete(`/api/admin/delete-user/${userId}`);
    return response.data;
  },

  adminToggleUser: async (userId) => {
    const response = await api.post(`/api/admin/toggle-user/${userId}`);
    return response.data;
  },

  adminSetUserSegments: async (userId, segments) => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.post(`/api/admin/user-segments/${encodeURIComponent(userId)}?token=${token}`, { segments });
    return response.data;
  },

  adminResetPassword: async (userId) => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.post(`/api/admin/reset-password/${encodeURIComponent(userId)}?token=${token}`);
    return response.data;
  },

  adminTradingOverview: async () => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.get(`/api/admin/trading-overview?token=${token}`);
    return response.data;
  },

  getGlobalContext: async () => {
    const token = localStorage.getItem('app_token') || '';
    // cache-bust (_ts) + no-store so the browser never serves a frozen copy
    const response = await api.get(`/api/global-context?token=${token}&_ts=${Date.now()}`,
      { headers: { 'Cache-Control': 'no-cache' } });
    return response.data;
  },

  adminPerformance: async () => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.get(`/api/admin/performance?token=${token}`);
    return response.data;
  },

  adminClientTrading: async (userId) => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.get(`/api/admin/client/${encodeURIComponent(userId)}/trading?token=${token}`);
    return response.data;
  },

  adminRecommendations: async (limit = 100) => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.get(`/api/admin/recommendations?token=${token}&limit=${limit}`);
    return response.data;
  },

  changePassword: async (token, oldPassword, newPassword) => {
    const response = await api.post(`/api/change-password?token=${token}`, {
      old_password: oldPassword,
      new_password: newPassword
    });
    return response.data;
  },

  getSymbols: async (exchange = '', segment = '') => {
    const params = new URLSearchParams();
    if (exchange) params.append('exchange', exchange);
    if (segment) params.append('segment', segment);
    const response = await api.get(`/api/symbols?${params.toString()}`);
    return response.data;
  },

  searchSymbols: async (q, exchange = '', segment = '', limit = 20) => {
    const params = new URLSearchParams();
    if (q) params.append('q', q);
    if (exchange) params.append('exchange', exchange);
    if (segment) params.append('segment', segment);
    if (limit) params.append('limit', limit);
    const response = await api.get(`/api/symbols/search?${params.toString()}`);
    return response.data;
  },

  weeklyReport: async () => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.get(`/api/weekly-report?token=${token}`);
    return response.data;
  },

  assistantRecommend: async (symbol) => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.get(`/api/assistant/recommend?token=${token}&symbol=${encodeURIComponent(symbol)}`);
    return response.data;
  },

  login: async (username, password, remember = false) => {
    const response = await api.post('/api/login', { username, password, remember });
    return response.data;
  },

  quickLogin: async () => {
    const response = await api.post('/api/login/quick');
    return response.data;
  },

  verifyOTP: async (otp) => {
    const response = await api.post('/api/verify-otp', { otp });
    return response.data;
  },

  verifyTOTP: async (totp) => {
    const response = await api.post('/api/verify-totp', { otp: totp });
    return response.data;
  },

  checkBroker: async () => {
    const response = await api.get('/api/broker/check');
    return response.data;
  },

  saveApiKey: async (api_key) => {
    const response = await api.post('/api/config/api-key', { api_key });
    return response.data;
  },

  getConfig: async () => {
    const response = await api.get('/api/config');
    return response.data;
  },

  updateConfig: async (config) => {
    const response = await api.post('/api/config', config);
    return response.data;
  },

  startTrading: async () => {
    const response = await api.post('/api/trading/start');
    return response.data;
  },

  stopTrading: async () => {
    const response = await api.post('/api/trading/stop');
    return response.data;
  },

  clearPositions: async () => {
    const response = await api.post('/api/trading/clear-positions');
    return response.data;
  },

  closePosition: async (symbol, qty = 0) => {
    // token identifies the caller — an ADMIN token can close ANY position (incl. a
    // client's SYMBOL#uid key); a client token can only close their own.
    const token = localStorage.getItem('app_token') || '';
    const response = await api.post(`/api/trading/close-position/${encodeURIComponent(symbol)}?token=${encodeURIComponent(token)}${qty > 0 ? `&qty=${qty}` : ''}`);
    return response.data;
  },

  closeAllPositions: async () => {
    const response = await api.post('/api/trading/close-all');
    return response.data;
  },

  holdPosition: async (symbol, hold) => {
    // Pin/unpin a position to manual hold — AI won't auto-exit when hold=true.
    const response = await api.post(`/api/trading/hold-position/${encodeURIComponent(symbol)}?hold=${hold ? 'true' : 'false'}`);
    return response.data;
  },

  getTradingMode: async () => {
    const response = await api.get('/api/trading/mode');
    return response.data;
  },

  setTradingMode: async (mode) => {
    const response = await api.post('/api/trading/mode', { mode, token: localStorage.getItem('app_token') || '' });
    return response.data;
  },

  getSegment: async () => {
    const response = await api.get('/api/trading/segment', { params: { token: localStorage.getItem('app_token') || '' } });
    return response.data;
  },

  getMlStatus: async () => {
    const response = await api.get('/api/ml/status', { params: { token: localStorage.getItem('app_token') || '' } });
    return response.data;
  },

  getNews: async () => {
    const response = await api.get('/api/news', { params: { token: localStorage.getItem('app_token') || '' } });
    return response.data;
  },

  getDecisionReview: async () => {
    const response = await api.get('/api/decision-review', { params: { token: localStorage.getItem('app_token') || '' } });
    return response.data;
  },

  getStockBackground: async () => {
    const response = await api.get('/api/admin/stock-background', { params: { token: localStorage.getItem('app_token') || '' } });
    return response.data;
  },
  getLearningBrain: async () => {
    const response = await api.get('/api/learning-brain', { params: { token: localStorage.getItem('app_token') || '' } });
    return response.data;
  },

  getSegmentAvailability: async () => {
    const response = await api.get('/api/segments/availability');
    return response.data;
  },

  setSegment: async (segment) => {
    const response = await api.post('/api/trading/segment', { segment, token: localStorage.getItem('app_token') || '' });
    return response.data;
  },

  assistantHealth: async () => {
    const response = await api.get('/api/assistant/health');
    return response.data;
  },

  assistantAsk: async (question) => {
    const response = await api.post('/api/assistant/ask', { question });
    return response.data;
  },

  userBrokerStatus: async () => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.get(`/api/user/broker/status?token=${token}`);
    return response.data;
  },

  userMstockSetup: async (api_key) => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.post('/api/user/broker/mstock/setup', { token, api_key });
    return response.data;
  },

  userMstockLogin: async (username, password) => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.post('/api/user/broker/mstock/login', { token, username, password });
    return response.data;
  },

  userMstockVerifyOtp: async (otp) => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.post('/api/user/broker/mstock/verify-otp', { token, otp });
    return response.data;
  },

  getDecisionsReport: async () => (await api.get('/api/admin/decisions-report')).data,
  getAutoTrade: async () => (await api.get('/api/admin/autotrade')).data,
  setAutoTrade: async (on) => (await api.post('/api/admin/autotrade', { on })).data,
  getAlertsOn: async () => (await api.get('/api/push/enabled')).data,
  setAlertsOn: async (on) => (await api.post('/api/push/enabled', { on })).data,
  getPushKey: async () => (await api.get('/api/push/vapid-public')).data,
  pushSubscribe: async (subscription) => (await api.post('/api/push/subscribe', { subscription })).data,
  getAdvice: async () => (await api.get('/api/admin/advice')).data,
  decideAdvice: async (id, action) => (await api.post(`/api/admin/advice/${encodeURIComponent(id)}`, { action })).data,
  getApprovals: async () => (await api.get('/api/admin/approvals')).data,
  decideApproval: async (id, action, force = false) => (await api.post(`/api/admin/approvals/${encodeURIComponent(id)}`, { action, force })).data,
  checkApproval: async (id) => (await api.get(`/api/admin/approvals/${encodeURIComponent(id)}/check`)).data,
  getAiOrderPreview: async (symbol) => (await api.get(`/api/my/ai-order-preview?symbol=${encodeURIComponent(symbol)}`)).data,

  myManualOptionOrder: async (underlying, opt_type, side, lots) =>
    (await api.post('/api/my/manual-option-order', { underlying, opt_type, side, lots })).data,

  myManualOrder: async (symbol, exchange, side, lots, lot_size, limit_price = 0) => {
    const response = await api.post('/api/my/manual-order', { symbol, exchange, side, lots, lot_size, limit_price });
    return response.data;
  },
  limitExit: async (key, price, qty = 0) => (await api.post(`/api/trading/limit-exit/${encodeURIComponent(key)}`, { price, qty })).data,
  getPendingOrders: async () => (await api.get('/api/my/pending-orders')).data,
  modifyPendingOrder: async (pid, price, qty) => (await api.post(`/api/my/pending-orders/${encodeURIComponent(pid)}/modify`, { price, qty })).data,
  cancelPendingOrder: async (pid) => (await api.post(`/api/my/pending-orders/${encodeURIComponent(pid)}/cancel`, {})).data,

  getStrategyIdeas: async () => {
    const response = await api.get('/api/my/strategy-ideas');
    return response.data;
  },
  implementStrategyIdea: async (id, lots = 1) => {
    const response = await api.post(`/api/my/strategy-ideas/${id}/implement`, { lots });
    return response.data;
  },
  ignoreStrategyIdea: async (id) => {
    const response = await api.post(`/api/my/strategy-ideas/${id}/ignore`, {});
    return response.data;
  },

  getMyCondors: async () => {
    const response = await api.get('/api/my/condors');
    return response.data;
  },

  getMyTradingPrefs: async () => {
    const response = await api.get('/api/my/trading-prefs');
    return response.data;
  },

  setMyTradingPrefs: async (prefs) => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.post('/api/my/trading-prefs', { token, ...prefs });
    return response.data;
  },

  getMyTradeLog: async (limit = 50) => {
    const response = await api.get(`/api/my/trade-log?limit=${limit}`);
    return response.data;
  },

  userBrokers: async () => {
    const response = await api.get('/api/user/brokers');
    return response.data;
  },

  userBrokerSaveConfig: async (brokerId, values) => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.post(`/api/user/brokers/${encodeURIComponent(brokerId)}/config`, { token, values });
    return response.data;
  },

  userFyersLoginUrl: async () => {
    const response = await api.get('/api/user/brokers/fyers/login-url');
    return response.data;
  },

  userFyersConnect: async (authCode) => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.post('/api/user/brokers/fyers/connect', { token, auth_code: authCode });
    return response.data;
  },

  userFyersAutoLogin: async (fy_id, pin, totp_secret) => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.post('/api/user/brokers/fyers/auto-login', { token, fy_id, pin, totp_secret });
    return response.data;
  },

  userFyersAutoLoginStatus: async () => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.get(`/api/user/brokers/fyers/auto-login/status?token=${token}`);
    return response.data;
  },

  userBrokerCheck: async (brokerId) => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.post(`/api/user/brokers/${encodeURIComponent(brokerId)}/check`, { token });
    return response.data;
  },

  userBrokerDisconnect: async (brokerId) => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.post(`/api/user/brokers/${encodeURIComponent(brokerId)}/disconnect`, { token });
    return response.data;
  },

  getEquityProduct: async () => {
    const response = await api.get('/api/trading/equity-product');
    return response.data;
  },

  setEquityProduct: async (product) => {
    const response = await api.post('/api/trading/equity-product', { product });
    return response.data;
  },

  getExchange: async () => {
    const response = await api.get('/api/trading/exchange');
    return response.data;
  },

  setExchange: async (exchange) => {
    const response = await api.post('/api/trading/exchange', { exchange, token: localStorage.getItem('app_token') || '' });
    return response.data;
  },

  getLiveBroker: async () => {
    const response = await api.get('/api/trading/broker');
    return response.data;
  },

  setLiveBroker: async (broker) => {
    const response = await api.post('/api/trading/broker', { broker, token: localStorage.getItem('app_token') || '' });
    return response.data;
  },

  getTrades: async (mode = null) => {
    const params = mode ? `?today_only=true&mode=${mode}` : '?today_only=true';
    const response = await api.get(`/api/trades${params}`);
    return response.data;
  },

  getPnL: async () => {
    const response = await api.get('/api/pnl?today_only=true');
    return response.data;
  },

  getFunds: async () => {
    const response = await api.get('/api/funds');
    return response.data;
  },

  getActivity: async () => {
    const response = await api.get('/api/activity', { params: { token: localStorage.getItem('app_token') || '' } });
    return response.data;
  },

  searchInstruments: async (q, exchange, segment, limit) => {
    const params = new URLSearchParams();
    if (q) params.append('q', q);
    if (exchange) params.append('exchange', exchange);
    if (segment) params.append('segment', segment);
    if (limit) params.append('limit', limit);
    const response = await api.get(`/api/search?${params.toString()}`);
    return response.data;
  },

  getIndices: async () => {
    const response = await api.get('/api/indices');
    return response.data;
  },

  getMcxCommodities: async () => {
    const response = await api.get('/api/mcx/commodities');
    return response.data;
  },

  getMcxExpiries: async (commodity) => {
    const response = await api.get(`/api/mcx/expiries/${encodeURIComponent(commodity)}`);
    return response.data;
  },

  getMcxFutures: async (commodity) => {
    const response = await api.get(`/api/mcx/futures/${encodeURIComponent(commodity)}`);
    return response.data;
  },

  getFoStocks: async () => {
    const response = await api.get('/api/fo-stocks');
    return response.data;
  },

  getWatchlistTechnicals: async (items) => {
    const response = await api.post('/api/watchlist/technicals', { items });
    return response.data;
  },

  getMarketStatus: async (exchange = 'NSE') => {
    const response = await api.get(`/api/market-status?exchange=${exchange}`);
    return response.data;
  },

  getCandles: async (symbol, exchange = 'NSE', period = '', interval = '5m') => {
    const params = new URLSearchParams({ exchange, interval });
    if (period) params.set('period', period);
    const response = await api.get(`/api/candles/${encodeURIComponent(symbol)}?${params.toString()}`);
    return response.data;
  },

  getCreditSpreads: async () => (await api.get('/api/credit-spreads')).data,
  getAiPicks: async () => (await api.get('/api/my/ai-picks')).data,
  getMissedTrades: async (days = 1) => (await api.get(`/api/admin/missed-trades?days=${days}`)).data,
  getChartAi: async (symbol) => (await api.get(`/api/chart-ai/${encodeURIComponent(symbol)}`)).data,
  getChart: async (symbol, tf = '5m', exchange = 'NSE', fill = 0) =>
    (await api.get(`/api/chart/${encodeURIComponent(symbol)}?tf=${encodeURIComponent(tf)}&exchange=${exchange}&fill=${fill}`)).data,
  getQuote: async (symbol, exchange = 'NSE') =>
    (await api.get(`/api/quote/${encodeURIComponent(symbol)}?exchange=${exchange}`)).data,
  getControl: async () => (await api.get('/api/admin/control')).data,
  decisionLab: async () => (await api.get('/api/admin/decision-lab')).data,
  setControl: async (key, value) => (await api.post('/api/admin/control', { key, value })).data,
  resetControl: async (key) => (await api.post('/api/admin/control/reset', { key })).data,
  getPresets: async () => (await api.get('/api/admin/control/presets')).data,
  applyPreset: async (name) => (await api.post('/api/admin/control/preset', { name })).data,

  getBigPlayer: async (symbol, segment = 'equity') => {
    const response = await api.get(`/api/big-player/${encodeURIComponent(symbol)}?segment=${segment}`);
    return response.data;
  },

  getOptionChain: async (underlying, exchange, expiry) => {
    const params = new URLSearchParams();
    if (exchange) params.append('exchange', exchange);
    if (expiry) params.append('expiry', expiry);
    const response = await api.get(`/api/options/${underlying}?${params.toString()}`);
    return response.data;
  },

  buyOption: async (symbol, exchange, quantity, price) => {
    const response = await api.post('/api/options/buy', { symbol, exchange, quantity, price });
    return response.data;
  },

  sellOption: async (symbol, exchange, quantity, price) => {
    const response = await api.post('/api/options/sell', { symbol, exchange, quantity, price });
    return response.data;
  },

  fyersStatus: async () => {
    const response = await api.get('/api/fyers/status');
    return response.data;
  },

  fyersSaveConfig: async (app_id, app_secret, redirect_uri) => {
    const response = await api.post('/api/fyers/config', { app_id, app_secret, redirect_uri });
    return response.data;
  },

  fyersAutoLoginConfig: async (fy_id, pin, totp_secret) => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.post('/api/fyers/auto-login/config', { token, fy_id, pin, totp_secret });
    return response.data;
  },

  fyersAutoLoginStatus: async () => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.get(`/api/fyers/auto-login/status?token=${token}`);
    return response.data;
  },

  // ── Separate Fyers TRADING API (distinct from the data feed) ──
  fyersTradeStatus: async () => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.get(`/api/fyers/trade/status?token=${token}`);
    return response.data;
  },
  fyersTradeConfig: async (app_id, app_secret) => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.post('/api/fyers/trade/config', { token, app_id, app_secret });
    return response.data;
  },
  fyersTradeLoginUrl: async () => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.get(`/api/fyers/trade/login-url?token=${token}`);
    return response.data;
  },
  fyersTradeConnect: async (auth_code) => {
    const token = localStorage.getItem('app_token') || '';
    const response = await api.post('/api/fyers/trade/connect', { token, auth_code });
    return response.data;
  },

  fyersLogin: async () => {
    const response = await api.get('/api/fyers/login');
    return response.data;
  },

  fyersSendOtp: async (fy_id) => {
    const response = await api.post('/api/fyers/send-otp', { fy_id });
    return response.data;
  },

  fyersVerifyOtp: async (otp) => {
    const response = await api.post('/api/fyers/verify-otp', { otp });
    return response.data;
  },

  fyersVerifyTotp: async (totp) => {
    const response = await api.post('/api/fyers/verify-totp', { totp });
    return response.data;
  },

  fyersVerifyPin: async (pin) => {
    const response = await api.post('/api/fyers/verify-pin', { pin });
    return response.data;
  },

  fyersGetAuthCode: async () => {
    const response = await api.get('/api/fyers/get-auth-code');
    return response.data;
  },

  fyersGenerateToken: async (auth_code) => {
    const response = await api.post('/api/fyers/generate-token', { auth_code });
    return response.data;
  },

  fyersMcxSymbol: async (commodity) => {
    const response = await api.get(`/api/fyers/mcx-symbol/${commodity}`);
    return response.data;
  },

  fyersMcxOptions: async (commodity, expiry) => {
    const params = expiry ? `?expiry=${expiry}` : '';
    const response = await api.get(`/api/fyers/mcx-options/${commodity}${params}`);
    return response.data;
  },

  fyersPlaceOrder: async (symbol, quantity, price, order_type, transaction_type, product) => {
    const response = await api.post('/api/fyers/place-order', { symbol, quantity, price, order_type, transaction_type, product });
    return response.data;
  },

  analyzeStock: async (symbol) => {
    const response = await api.get(`/api/analysis/${symbol}`);
    return response.data;
  },

  getWatchlistPrices: async (items) => {
    const response = await api.post('/api/watchlist/prices', { items });
    return response.data;
  },

  logout: async () => {
    // BROKER disconnect (Settings) — disconnects the master broker account.
    const response = await api.post('/api/logout');
    return response.data;
  },

  appLogout: async () => {
    // APP logout — ends THIS user's session so they go offline (no more trades).
    // The token is attached automatically by the request interceptor.
    const response = await api.post('/api/app/logout');
    return response.data;
  },

  listBrokers: async () => {
    const response = await api.get('/api/brokers');
    return response.data;
  },

  saveBrokerConfig: async (brokerId, values) => {
    const response = await api.post(`/api/brokers/${brokerId}/config`, { values });
    return response.data;
  },

  killSwitch: async () => {
    const response = await api.post('/api/trading/kill');
    return response.data;
  },

  exportTrades: async (token = '') => {
    const response = await api.get(`/api/trades/export?token=${token}`, { responseType: 'blob' });
    return response.data;
  },

  getSegmentHalt: async () => {
    const response = await api.get('/api/admin/segment-halt');
    return response.data;
  },

  setSegmentHalt: async (segment, halt) => {
    const response = await api.post('/api/admin/segment-halt', { segment, halt });
    return response.data;
  },

  getHealth: async () => {
    const response = await api.get('/api/admin/health');
    return response.data;
  },
};

export default tradingAPI;
